'use client';

import {
  Fragment,
  forwardRef,
  useEffect,
  useRef,
  useState,
  type Ref,
} from 'react';
import type { Assign } from '@ark-ui/react';
import { Menu, Portal } from '@ark-ui/react';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import {
  type MenuVariantProps,
  menu,
  Box,
  token,
} from '@terra/ui-styled-system';
import { createStyleContext } from './utils/create-style-context';
import { Tick01Icon, ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { MenuEmptyState } from './menu-empty-state';
import { useTranslations } from 'next-intl';
import { Checkbox } from '../checkbox';
import { IconButton } from '../icon-button';
import { FilterOptionHelpTooltip } from '../filter-option-help-tooltip';
import { Tooltip } from '../tooltip';
import { automationId } from '@terra/shared-utils';

const { withRootProvider, withContext } = createStyleContext(menu);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withRootProvider<
  Assign<Menu.RootProviderProps, MenuVariantProps>
>(Menu.RootProvider);
const MenuRootWithDefaults = (
  props: Assign<Menu.RootProps, MenuVariantProps>,
) => {
  const positioning: Menu.RootProps['positioning'] = {
    strategy: 'fixed',
    ...props.positioning,
  };

  return <Menu.Root {...props} positioning={positioning} />;
};

export type RootProps = ComponentProps<typeof Root>;
/**
 * @deprecated Use MenuWithItems instead. This component is deprecated and will be removed in a future version.
 * MenuWithItems provides a simpler API for creating menus with items.
 */
export const Root =
  withRootProvider<Assign<Menu.RootProps, MenuVariantProps>>(
    MenuRootWithDefaults,
  );
export const Arrow = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ArrowProps>
>(Menu.Arrow, 'arrow');
export const ArrowTip = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ArrowTipProps>
>(Menu.ArrowTip, 'arrowTip');
export const CheckboxItem = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.CheckboxItemProps>
>(Menu.CheckboxItem, 'item');
export const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ContentProps>
>(Menu.Content, 'content');
export const ContextTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Menu.ContextTriggerProps>
>(Menu.ContextTrigger, 'contextTrigger');
export const Indicator = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.IndicatorProps>
>(Menu.Indicator, 'indicator');
export const ItemGroupLabel = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ItemGroupLabelProps>
>(Menu.ItemGroupLabel, 'itemGroupLabel');
export const ItemGroup = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ItemGroupProps>
>(Menu.ItemGroup, 'itemGroup');
export const ItemIndicator = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ItemIndicatorProps>
>(Menu.ItemIndicator, 'itemIndicator');
const StyledItem = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ItemProps>
>(Menu.Item, 'item');

export function Item({
  ref,
  disabled,
  onClick,
  onPointerDown,
  onKeyDown,
  ...props
}: ComponentProps<typeof StyledItem> & { ref?: Ref<HTMLDivElement> }) {
  return (
    <StyledItem
      {...props}
      ref={ref}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onPointerDown={disabled ? undefined : onPointerDown}
      onKeyDown={
        disabled
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
              }
            }
          : onKeyDown
      }
    />
  );
}

export const ItemText = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.ItemTextProps>
>(Menu.ItemText, 'itemText');
export const Positioner = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.PositionerProps>
>(Menu.Positioner, 'positioner');
export const RadioItemGroup = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.RadioItemGroupProps>
>(Menu.RadioItemGroup, 'itemGroup');
export const RadioItem = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.RadioItemProps>
>(Menu.RadioItem, 'item');
export const Separator = withContext<
  HTMLHRElement,
  Assign<HTMLStyledProps<'hr'>, Menu.SeparatorProps>
>(Menu.Separator, 'separator');
export const TriggerItem = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Menu.TriggerItemProps>
>(Menu.TriggerItem, 'triggerItem');
const BaseTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Menu.TriggerProps>
>(Menu.Trigger, 'trigger');

export const Trigger = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof BaseTrigger>
>((props, ref) => <BaseTrigger ref={ref} data-bi-unmask="" {...props} />);
Trigger.displayName = 'Menu.Trigger';

export { MenuContext as Context } from '@ark-ui/react';
export type SelectionIndicator = 'checkbox' | 'checkmark';
export type MenuContextApi = Parameters<
  NonNullable<React.ComponentProps<typeof Menu.Context>['children']>
>[0];
export type MenuItemProps = {
  value: string;
  label: React.ReactNode;
  tooltip?: string;
  onClick: (value: string) => void;
  selected?: boolean | 'indeterminate';
  disabled?: boolean;
  selectionIndicator?: SelectionIndicator;
  withSeparator?: boolean;
  nestedItems?: MenuItemProps[];
  isInitiallyExpanded?: boolean;
  isNested?: boolean;
  expandOnRowClick?: boolean;
  hideExpandButton?: boolean;
  disableHover?: boolean;
  labelTooltip?: string;
  dataTestid?: string;
};
export type MenuWithItemsProps = {
  items: MenuItemProps[];
  trigger: React.ReactNode;
  emptyState?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  headerStyles?: React.CSSProperties;
  footerStyles?: React.CSSProperties;
  selectionIndicator?: SelectionIndicator;
  className?: string;
  contentWidth?: string;
  portalled?: boolean;
  onExposeMenuApi?: (contextApi: MenuContextApi) => void;
} & Omit<Menu.RootProps, 'children'>;
type ExposeMenuApiEffectProps = {
  contextApi: MenuContextApi;
  onExposeMenuApi?: (contextApi: MenuContextApi) => void;
};
const ExposeMenuApiEffect = ({
  contextApi,
  onExposeMenuApi,
}: ExposeMenuApiEffectProps) => {
  const lastExposedApiRef = useRef<MenuContextApi | null>(null);

  useEffect(() => {
    if (!onExposeMenuApi) {
      return;
    }

    if (lastExposedApiRef.current === contextApi) {
      return;
    }

    lastExposedApiRef.current = contextApi;
    onExposeMenuApi(contextApi);
  }, [contextApi, onExposeMenuApi]);

  return null;
};

export const MenuItem = ({
  value,
  label,
  tooltip,
  selected,
  disabled,
  onClick,
  selectionIndicator = 'checkmark',
  nestedItems,
  isInitiallyExpanded,
  isNested,
  expandOnRowClick = false,
  hideExpandButton = false,
  disableHover = false,
  labelTooltip,
  dataTestid,
}: MenuItemProps) => {
  const t = useTranslations('designSystem.menu');
  const [expanded, setExpanded] = useState(isInitiallyExpanded ?? false);
  const hasNestedItems = (nestedItems?.length ?? 0) > 0;
  const labelContent = labelTooltip ? (
    <Tooltip title={labelTooltip} maxW="280px">
      <Box as="span" display="block" truncate minW="0" maxW="full">
        {label}
      </Box>
    </Tooltip>
  ) : (
    label
  );

  return (
    <>
      <Item
        value={value}
        disabled={disabled}
        {...(dataTestid ? automationId(dataTestid) : {})}
        onClick={(event) => {
          if (disabled) {
            return;
          }

          if (hasNestedItems && expandOnRowClick) {
            event.preventDefault();
            event.stopPropagation();
            setExpanded((currentExpanded) => !currentExpanded);

            return;
          }

          onClick(value);
        }}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="2"
        px="3"
        py="2"
        pl={isNested ? '9' : '3'}
        m="0"
        height="auto"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        color={disabled ? 'fg.neutral.disabled' : 'fg.default'}
        _hover={
          disableHover
            ? undefined
            : {
                bg: disabled ? 'transparent' : 'bg.neutral.hover',
              }
        }
      >
        {selectionIndicator === 'checkbox' && (
          <Box
            flexShrink={0}
            as="span"
            pointerEvents={expandOnRowClick ? 'auto' : 'none'}
            onClick={(event) => {
              if (disabled || !expandOnRowClick) {
                return;
              }

              event.stopPropagation();
              onClick(value);
            }}
            css={{
              '& svg': {
                color: 'unset !important',
              },
            }}
          >
            <Checkbox checked={selected} disabled={disabled} size="sm" />
          </Box>
        )}
        <Box flex="1" minW="0" display="flex" alignItems="center" gap="2">
          <Box
            display="inline-flex"
            alignItems="center"
            gap="2"
            minW={tooltip && !labelTooltip ? 'max-content' : '0'}
            maxW="full"
            whiteSpace={tooltip && !labelTooltip ? 'nowrap' : undefined}
          >
            <Box minW={tooltip && !labelTooltip ? undefined : '0'}>
              {labelContent}
            </Box>
            {tooltip && !labelTooltip ? (
              <FilterOptionHelpTooltip title={tooltip} />
            ) : null}
          </Box>
          {hasNestedItems && !hideExpandButton ? (
            expandOnRowClick ? (
              <Box
                flexShrink={0}
                ml="auto"
                display="flex"
                alignItems="center"
                color={disabled ? 'fg.neutral.disabled' : 'fg.default'}
                aria-hidden
              >
                {expanded ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
              </Box>
            ) : (
              <IconButton
                variant="ghost"
                size="xs"
                flexShrink={0}
                disabled={disabled}
                stopPropagation
                aria-expanded={expanded}
                aria-label={expanded ? t('collapse') : t('expand')}
                onClick={(event) => {
                  if (!disabled) {
                    event.stopPropagation();
                    setExpanded((currentExpanded) => !currentExpanded);
                  }
                }}
                ml="auto"
              >
                {expanded ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
              </IconButton>
            )
          ) : null}
        </Box>
        {selectionIndicator === 'checkmark' && selected === true && (
          <Box flexShrink={0}>
            <Tick01Icon
              size={16}
              color={token(
                disabled ? 'colors.fg.neutral.disabled' : 'colors.fg.default',
              )}
            />
          </Box>
        )}
      </Item>
      {expanded &&
        nestedItems?.map((nestedItem) => (
          <MenuItem
            key={nestedItem.value}
            {...nestedItem}
            isNested
            selectionIndicator={
              nestedItem.selectionIndicator ?? selectionIndicator
            }
          />
        ))}
    </>
  );
};

type MenuItemTreeProps = {
  item: MenuItemProps;
  selectionIndicator: SelectionIndicator;
};
const MenuItemTree = ({ item, selectionIndicator }: MenuItemTreeProps) => {
  const {
    withSeparator,
    selectionIndicator: itemSelectionIndicator,
    ...menuItemProps
  } = item;

  return (
    <Fragment key={item.value}>
      <MenuItem
        {...menuItemProps}
        selectionIndicator={itemSelectionIndicator ?? selectionIndicator}
      />
      {withSeparator && <Separator />}
    </Fragment>
  );
};

export const MenuWithItems = ({
  items,
  trigger,
  header: HeaderComponent,
  footer: FooterComponent,
  emptyState: EmptyStateComponent,
  headerStyles,
  footerStyles,
  selectionIndicator = 'checkmark',
  className,
  onExposeMenuApi,
  contentWidth = '280px',
  portalled = false,
  ...rootProps
}: MenuWithItemsProps) => (
  <Root lazyMount unmountOnExit {...rootProps}>
    <Menu.Context>
      {(contextApi) => {
        const positioner = (
          <Positioner>
            <Content p="0" gap="0" w={contentWidth} className={className}>
              {HeaderComponent && (
                <Box
                  p="2"
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  style={headerStyles}
                >
                  {HeaderComponent}
                </Box>
              )}
              {items.length > 0 ? (
                <ItemGroup maxHeight="204px" overflowY="auto" p="1">
                  {items.map((item) => (
                    <MenuItemTree
                      key={item.value}
                      item={item}
                      selectionIndicator={selectionIndicator}
                    />
                  ))}
                </ItemGroup>
              ) : (
                EmptyStateComponent || <MenuEmptyState />
              )}
              {FooterComponent && (
                <Box
                  p="2"
                  borderTopWidth="1px"
                  borderColor="border.subtle"
                  style={footerStyles}
                >
                  {FooterComponent}
                </Box>
              )}
            </Content>
          </Positioner>
        );

        return (
          <>
            <ExposeMenuApiEffect
              contextApi={contextApi}
              onExposeMenuApi={onExposeMenuApi}
            />
            <Trigger asChild>{trigger}</Trigger>
            {portalled ? <Portal>{positioner}</Portal> : positioner}
          </>
        );
      }}
    </Menu.Context>
  </Root>
);
